import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Download, Share2, Copy, Trash2, AlertTriangle } from "lucide-react";
import { api, apiError, API } from "@/lib/api";
import { brl, fmtDate, statusLabel, statusColor, phoneDigits } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function QuoteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [q, setQ] = useState(null);
  const [waOpen, setWaOpen] = useState(false);
  const [waMsg, setWaMsg] = useState("");

  const load = () => api.get(`/quotes/${id}`).then((r) => setQ(r.data)).catch(() => toast.error("Orçamento não encontrado."));
  useEffect(() => { load(); }, [id]);

  if (!q) return <div className="py-20 text-center text-slate-400">Carregando...</div>;

  const calc = q.calc || {};
  const customer = q.customer || {};
  const company = q.company || {};

  const setStatus = async (status) => {
    try {
      await api.put(`/quotes/${id}/status`, { status });
      toast.success("Status atualizado.");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const duplicate = async () => {
    const { data } = await api.post(`/quotes/${id}/duplicate`);
    toast.success(`Orçamento duplicado como ${data.number}.`);
    navigate(`/app/orcamentos/${data.id}`);
  };

  const remove = async () => {
    if (!window.confirm("Excluir este orçamento?")) return;
    await api.delete(`/quotes/${id}`);
    toast.success("Orçamento excluído.");
    navigate("/app/orcamentos");
  };

  const openWhatsApp = () => {
    const msg = `Olá, ${customer.name || "tudo bem"}! Conforme nossa avaliação, segue seu orçamento ${q.number} para ${q.service_type}${q.equipment?.type ? ` no equipamento ${q.equipment.type}` : ""}.\n\nValor total: ${brl(calc.preco_final)}\nValidade: ${q.validity_days} dias\nGarantia: ${q.warranty || "-"}\nPagamento: ${q.payment_terms || "-"}\n\nO documento apresenta os serviços, materiais, valores, prazo e condições. Qualquer dúvida, estou à disposição.\n\n— ${company.name || ""}`;
    setWaMsg(msg);
    setWaOpen(true);
  };

  const sendWhatsApp = async () => {
    const phone = phoneDigits(customer.whatsapp || customer.phone);
    const url = `https://wa.me/${phone ? "55" + phone : ""}?text=${encodeURIComponent(waMsg)}`;
    window.open(url, "_blank");
    await setStatus("enviado");
    setWaOpen(false);
  };

  return (
    <div className="max-w-3xl mx-auto space-y-5 animate-in-up" data-testid="quote-detail">
      <Link to="/app/orcamentos" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800" data-testid="back-quotes">
        <ArrowLeft className="h-4 w-4" /> Orçamentos
      </Link>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{q.number}</h1>
          <p className="text-sm text-slate-500">{q.service_type} · criado em {fmtDate(q.created_at)}</p>
        </div>
        <Badge variant="outline" className={`${statusColor[q.status]}`} data-testid="quote-status">{statusLabel[q.status]}</Badge>
      </div>

      {calc.alertas?.map((a, i) => (
        <div key={i} className="bg-red-50 border border-red-200 rounded-lg p-3 flex gap-2 text-sm text-red-800" data-testid={`quote-alert-${i}`}>
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /> {a}
        </div>
      ))}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Button className="h-12 bg-sky-600 hover:bg-sky-700 text-white" onClick={() => window.open(`${API}/quotes/${id}/pdf`, "_blank")} data-testid="quote-pdf-btn">
          <Download className="h-4 w-4 mr-1.5" /> PDF
        </Button>
        <Button className="h-12 bg-green-600 hover:bg-green-700 text-white" onClick={openWhatsApp} data-testid="quote-whatsapp-btn">
          <Share2 className="h-4 w-4 mr-1.5" /> WhatsApp
        </Button>
        <Button variant="outline" className="h-12" onClick={duplicate} data-testid="quote-duplicate-btn">
          <Copy className="h-4 w-4 mr-1.5" /> Duplicar
        </Button>
        <Button variant="outline" className="h-12 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700" onClick={remove} data-testid="quote-delete-btn">
          <Trash2 className="h-4 w-4 mr-1.5" /> Excluir
        </Button>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader><CardTitle className="text-base">Status do orçamento</CardTitle></CardHeader>
        <CardContent>
          <Select value={q.status} onValueChange={setStatus}>
            <SelectTrigger className="h-12" data-testid="quote-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="rascunho">Rascunho</SelectItem>
              <SelectItem value="enviado">Enviado</SelectItem>
              <SelectItem value="aprovado">Aprovado</SelectItem>
              <SelectItem value="recusado">Recusado</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader><CardTitle className="text-base">Cliente e equipamento</CardTitle></CardHeader>
        <CardContent className="text-sm space-y-1 text-slate-700">
          <p><b>{customer.name}</b></p>
          {customer.phone && <p>{customer.phone}</p>}
          {customer.address && <p>{customer.address}</p>}
          {q.equipment?.type && (
            <p className="pt-2 border-t border-slate-100 mt-2">
              {[q.equipment.type, q.equipment.brand, q.equipment.model, q.equipment.capacity && `${q.equipment.capacity} BTUs`, q.equipment.refrigerant].filter(Boolean).join(" · ")}
            </p>
          )}
          {q.description && <p className="pt-2 text-slate-600">{q.description}</p>}
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader><CardTitle className="text-base">Composição do preço</CardTitle></CardHeader>
        <CardContent className="font-mono-num text-sm space-y-1.5">
          {[["Mão de obra", calc.custo_mao_obra], ["Deslocamento", calc.custo_deslocamento],
            ["Peças", calc.custo_pecas], ["Outros custos", calc.outros_custos]].map(([l, v]) => (
            <div key={l} className="flex justify-between border-b border-slate-100 pb-1.5">
              <span className="text-slate-600">{l}</span><span>{brl(v)}</span>
            </div>
          ))}
          {q.parts?.map((p, i) => (
            <div key={i} className="flex justify-between text-xs text-slate-500 pl-4">
              <span>· {p.name} (x{p.qty})</span><span>{brl(p.qty * p.cost)}</span>
            </div>
          ))}
          <div className="flex justify-between border-b border-slate-200 pb-1.5 font-semibold">
            <span>Custo total</span><span>{brl(calc.custo_total)}</span>
          </div>
          <div className="flex justify-between border-b border-slate-100 pb-1.5">
            <span className="text-slate-600">Impostos</span><span>{brl(calc.impostos)}</span>
          </div>
          {calc.desconto > 0 && (
            <div className="flex justify-between border-b border-slate-100 pb-1.5 text-green-700">
              <span>Desconto</span><span>- {brl(calc.desconto)}</span>
            </div>
          )}
          <div className="flex justify-between pt-2 text-base font-semibold">
            <span>Preço final</span><span className="text-sky-700" data-testid="quote-total">{brl(calc.preco_final)}</span>
          </div>
          <div className="flex justify-between text-green-700">
            <span>Lucro estimado</span><span>{brl(calc.lucro_estimado)} · {calc.margem_real}%</span>
          </div>
        </CardContent>
      </Card>

      {q.market_ref?.range_label && (
        <Card className="border-sky-200 bg-sky-50" data-testid="quote-market-ref">
          <CardContent className="p-4 text-sm text-sky-900">
            Referência regional: <b className="font-mono-num">{q.market_ref.range_label}</b> — baseada em{" "}
            {q.market_ref.count} contribuições, atualizada em {q.market_ref.updated_label}. Referência informativa, não vinculante.
          </CardContent>
        </Card>
      )}

      <Card className="border-slate-200 shadow-sm">
        <CardHeader><CardTitle className="text-base">Condições</CardTitle></CardHeader>
        <CardContent className="text-sm text-slate-700 space-y-1">
          <p>Validade: {q.validity_days} dias</p>
          <p>Garantia: {q.warranty || "-"}</p>
          <p>Pagamento: {q.payment_terms || "-"}</p>
          {q.notes && <p className="text-slate-500 italic">{q.notes}</p>}
        </CardContent>
      </Card>

      <Dialog open={waOpen} onOpenChange={setWaOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Enviar pelo WhatsApp</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">Revise e edite a mensagem antes de enviar. O orçamento será marcado como enviado.</p>
          <Textarea rows={10} value={waMsg} onChange={(e) => setWaMsg(e.target.value)} data-testid="whatsapp-message" />
          <Button className="w-full h-12 bg-green-600 hover:bg-green-700 text-white" onClick={sendWhatsApp} data-testid="whatsapp-send-btn">
            Abrir WhatsApp
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
